import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Box,
    Button,
    Code,
    Icon,
    List,
    ListItem,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Stack,
    Text,
    useToast,
} from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { BiCheckCircle, BiUpload } from 'react-icons/bi';

import { SchwabRmdExportUploadResult, useUploadSchwabRmdExport } from '../api/custodianRmd';
import { preflightSchwabRmdExport, SchwabRmdExportPreflight } from './schwabRmdExport';

interface SelectedFile {
    file: File;
    preflight: SchwabRmdExportPreflight;
}

/**
 * Lets IOPS upload the Schwab Advisor Center "RMD All Ages" export (browse or drag and drop).
 * The file is validated on the client for immediate feedback, then sent to the portfolio-optimizer which
 * validates it again and stores it in S3 for the Schwab load DAG.
 */
export const SchwabRmdUploadModal = ({ onClose }: { onClose: () => void }) => {
    const toast = useToast();
    const [selected, setSelected] = useState<SelectedFile | undefined>();
    const [result, setResult] = useState<SchwabRmdExportUploadResult | undefined>();
    const { mutate: upload, isPending, error, reset } = useUploadSchwabRmdExport();

    const onDrop = useCallback(
        async (files: File[]) => {
            reset();
            setResult(undefined);
            const file = files[0];
            if (!file) {
                return;
            }
            const text = await file.text();
            const preflight = preflightSchwabRmdExport(text);
            setSelected({ file, preflight });
        },
        [reset],
    );

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop,
        multiple: false,
        noClick: true,
        accept: { 'text/csv': ['.csv'], 'text/plain': ['.csv', '.txt'] },
        disabled: isPending,
    });

    const canUpload = !!selected && selected.preflight.problems.length === 0 && !isPending && !result;

    const handleUpload = () => {
        if (!selected) {
            return;
        }
        upload(
            { file: selected.file },
            {
                onSuccess: (uploaded) => {
                    setResult(uploaded);
                    toast({
                        title: 'Schwab RMD export uploaded',
                        description: `${uploaded.recordCount} rows stored for trade date ${uploaded.tradeDate}`,
                        status: 'success',
                        duration: 5000,
                    });
                },
            },
        );
    };

    return (
        <Modal isOpen onClose={onClose} size="xl" closeOnOverlayClick={!isPending}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Upload Schwab RMD export</ModalHeader>
                <ModalCloseButton isDisabled={isPending} />
                <ModalBody>
                    <Stack spacing={4}>
                        <Text fontSize="sm" color="gray.500">
                            Export the <b>RMD All Ages</b> report from Schwab Advisor Center and drop the downloaded
                            file here, as is. It is stored for the previous business day&apos;s Schwab load, which
                            parses and loads it from there.
                        </Text>

                        <Box
                            {...getRootProps()}
                            borderWidth={2}
                            borderStyle="dashed"
                            borderRadius="md"
                            borderColor={isDragActive ? 'teal.400' : 'gray.300'}
                            bg={isDragActive ? 'teal.50' : undefined}
                            p={8}
                            textAlign="center"
                            cursor={isPending ? 'not-allowed' : 'pointer'}
                            onClick={isPending ? undefined : open}
                            data-testid="schwab-rmd-dropzone"
                        >
                            <input {...getInputProps()} data-testid="schwab-rmd-file-input" />
                            <Icon as={BiUpload} boxSize={8} color="gray.400" />
                            <Text mt={2}>
                                {isDragActive
                                    ? 'Drop the file to select it'
                                    : 'Drag and drop the export here, or click to browse'}
                            </Text>
                            {selected && (
                                <Text mt={2} fontSize="sm" fontWeight="semibold">
                                    {selected.file.name} ({Math.max(1, Math.round(selected.file.size / 1024))} KB)
                                </Text>
                            )}
                        </Box>

                        {selected && selected.preflight.problems.length > 0 && (
                            <Alert status="error" alignItems="flex-start">
                                <AlertIcon />
                                <Box>
                                    <AlertTitle>This does not look like the RMD All Ages export</AlertTitle>
                                    <AlertDescription>
                                        <List spacing={1} mt={1} fontSize="sm">
                                            {selected.preflight.problems.map((problem) => (
                                                <ListItem key={problem}>{problem}</ListItem>
                                            ))}
                                        </List>
                                    </AlertDescription>
                                </Box>
                            </Alert>
                        )}

                        {selected && selected.preflight.problems.length === 0 && !result && (
                            <Alert status="info" fontSize="sm">
                                <AlertIcon />
                                {selected.preflight.recordCount} records
                                {selected.preflight.asOfLabel ? `, report as of ${selected.preflight.asOfLabel}` : ''}
                            </Alert>
                        )}

                        {error && (
                            <Alert status="error" fontSize="sm">
                                <AlertIcon />
                                <AlertDescription>{error.message}</AlertDescription>
                            </Alert>
                        )}

                        {result && (
                            <Alert status="success" alignItems="flex-start">
                                <AlertIcon as={BiCheckCircle} />
                                <Box fontSize="sm">
                                    <AlertTitle>Uploaded</AlertTitle>
                                    <AlertDescription>
                                        {result.recordCount} rows stored for trade date <b>{result.tradeDate}</b> as{' '}
                                        <Code fontSize="xs">{result.s3Key}</Code>
                                    </AlertDescription>
                                </Box>
                            </Alert>
                        )}
                    </Stack>
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button variant="ghost" onClick={onClose} isDisabled={isPending}>
                        {result ? 'Close' : 'Cancel'}
                    </Button>
                    {!result && (
                        <Button
                            colorScheme="teal"
                            leftIcon={<BiUpload />}
                            onClick={handleUpload}
                            isDisabled={!canUpload}
                            isLoading={isPending}
                            loadingText="Uploading"
                        >
                            Upload
                        </Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
